import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { clearIdentityCache } from '@/shared/api/query-client';

export interface CurrentUser {
  userId: number;
  email: string;
  roles: string[];
  permissions: string[];
  employeeId: number | null;
  sessionExpiresAt: string;
}

/**
 * Holds who is signed in, for rendering decisions only.
 *
 * FRONT-006 — the permission list here decides what the interface *shows*. It never
 * decides what is *allowed*: the server re-checks every request, so hiding a button is
 * courtesy, not a control.
 *
 * WEB-008 — nothing is persisted to localStorage. The session lives in an HttpOnly
 * cookie the page cannot read, and this state is rebuilt from `/auth/me` on load.
 */
export const useSessionStore = defineStore('session', () => {
  const user = ref<CurrentUser | null>(null);
  const resolved = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  /**
   * Cambiar de identidad descarta lo que la anterior tenía pedido.
   *
   * Acá y no en cada pantalla porque este es el único lugar por donde pasa un cambio de
   * usuario: repartirlo entre la vista de login, el cierre de sesión y el manejo del 401
   * era garantizar que alguno quedara afuera. Y quedó afuera: entrar con otra cuenta
   * mostraba el panel del usuario anterior hasta apretar F5.
   */
  function set(current: CurrentUser | null): void {
    if (current?.userId !== user.value?.userId) {
      clearIdentityCache();
    }

    user.value = current;
    resolved.value = true;
  }

  function clear(): void {
    if (user.value !== null) {
      clearIdentityCache();
    }

    user.value = null;
    resolved.value = true;
  }

  function can(permission: string): boolean {
    return user.value?.permissions.includes(permission) ?? false;
  }

  return { user, resolved, isAuthenticated, set, clear, can };
});
